/**
 * useWorkspaceConfig Hook
 *
 * Manages workspace configuration persistence with a write-through cache strategy.
 * Uses localStorage as a buffer for immediate reads/writes and syncs to the backend API.
 *
 * Strategy:
 * - On mount: Load from user-scoped localStorage, then fetch from API and reconcile
 * - On change: Write to localStorage immediately, debounce API sync
 * - On API failure: Continue using localStorage, queue retry
 * - Reconciliation: Use updated_at timestamp to determine which is newer
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { AUTH_STORAGE_KEYS } from "@/constants/authStorageKeys";
import {
  LEGACY_OS_STATE_KEY,
  getOsStateStorageKey,
  removeLegacyOsStateKey,
} from "@/constants/osStorageKeys";
import {
  getWorkspaceConfig,
  saveWorkspaceConfig,
} from "@/services/api/workspace-config";
import type {
  WorkspaceConfigRequest,
  WorkspaceConfigLocalStorage,
} from "@/services/api/workspace-config/types";
import { mergeWorkspaceConfigFromApi } from "@/utils/workspaceConfigMerge";

const SYNC_DEBOUNCE_MS = 2000; // 2 seconds - long debounce for background sync
const RETRY_DELAY_MS = 5000;
const MAX_RETRIES = 3;

/**
 * Backend validates window geometry as integers; DOM layout and legacy localStorage may contain floats.
 */
function sanitizeWorkspaceConfigForApi(
  config: WorkspaceConfigRequest
): WorkspaceConfigRequest {
  return {
    ...config,
    nextZIndex:
      config.nextZIndex !== undefined && config.nextZIndex !== null
        ? Math.round(Number(config.nextZIndex))
        : config.nextZIndex,
    windows: config.windows.map((w) => ({
      ...w,
      position: {
        x: Math.round(Number(w.position.x)),
        y: Math.round(Number(w.position.y)),
      },
      size: {
        width: Math.round(Number(w.size.width)),
        height: Math.round(Number(w.size.height)),
      },
      zIndex: Math.round(Number(w.zIndex)),
    })),
  };
}

/**
 * Deep equality check for workspace configs.
 * Used to skip no-op saves when config hasn't actually changed.
 */
function configsEqual(
  a: WorkspaceConfigRequest | null,
  b: WorkspaceConfigRequest,
): boolean {
  if (!a) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

function parseRawStorage(raw: string): WorkspaceConfigLocalStorage | null {
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;

    // Legacy format (direct config without wrapper)
    if (parsed.workspaces && !parsed.config) {
      return {
        ownerUserId: "",
        config: parsed as unknown as WorkspaceConfigRequest,
        hasPendingChanges: true,
      };
    }

    return parsed as unknown as WorkspaceConfigLocalStorage;
  } catch {
    return null;
  }
}

function readStorageKey(key: string): WorkspaceConfigLocalStorage | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    return parseRawStorage(stored);
  } catch (error) {
    console.error("[useWorkspaceConfig] Failed to load from localStorage:", error);
    return null;
  }
}

function getCachedWorkosUserId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEYS.WORKOS_USER);
    if (!raw) return null;
    const profile = JSON.parse(raw) as { id?: string };
    return profile.id ?? null;
  } catch {
    return null;
  }
}

/**
 * Load config for the current user, with legacy key migration when safe.
 */
function loadFromLocalStorage(userId: string): WorkspaceConfigLocalStorage | null {
  const storageKey = getOsStateStorageKey(userId);
  const scoped = readStorageKey(storageKey);

  if (scoped) {
    if (scoped.ownerUserId && scoped.ownerUserId !== userId) {
      try {
        localStorage.removeItem(storageKey);
      } catch {
        // ignore
      }
      return null;
    }
    return { ...scoped, ownerUserId: userId };
  }

  // One-time migration from legacy global key
  const legacy = readStorageKey(LEGACY_OS_STATE_KEY);
  if (!legacy) return null;

  const cachedUserId = getCachedWorkosUserId();
  const canMigrate =
    !legacy.ownerUserId &&
    cachedUserId === userId;

  removeLegacyOsStateKey();

  if (!canMigrate) {
    return null;
  }

  const migrated: WorkspaceConfigLocalStorage = {
    ...legacy,
    ownerUserId: userId,
  };

  saveToLocalStorage(userId, migrated);
  return migrated;
}

/**
 * Save config to user-scoped localStorage with sync metadata
 */
function saveToLocalStorage(
  userId: string,
  data: Omit<WorkspaceConfigLocalStorage, "ownerUserId"> & {
    ownerUserId?: string;
  },
): void {
  if (typeof window === "undefined") return;
  try {
    const payload: WorkspaceConfigLocalStorage = {
      ...data,
      ownerUserId: userId,
    };
    localStorage.setItem(
      getOsStateStorageKey(userId),
      JSON.stringify(payload),
    );
  } catch (error) {
    console.error("[useWorkspaceConfig] Failed to save to localStorage:", error);
  }
}

/**
 * Default workspace configuration for new users
 */
function getDefaultConfig(): WorkspaceConfigRequest {
  return {
    workspaces: [{ id: "ws-1", name: "Workspace 1", themeSeed: "cosmos" }],
    activeWorkspaceId: "ws-1",
    windows: [],
    nextZIndex: 100,
    dockIconStyle: "colored",
  };
}

export interface UseWorkspaceConfigResult {
  /** Current workspace configuration */
  config: WorkspaceConfigRequest | null;
  /** Whether initial load is in progress */
  isLoading: boolean;
  /** Whether a sync to the API is in progress */
  isSyncing: boolean;
  /** Last error that occurred */
  error: Error | null;
  /** Whether there are changes pending sync to the API */
  hasPendingChanges: boolean;
  /** Save configuration (writes to localStorage immediately, debounces API sync) */
  saveConfig: (config: WorkspaceConfigRequest) => void;
  /** Force an immediate sync to the API */
  forceSync: () => Promise<void>;
}

/**
 * Hook for managing workspace configuration with hybrid localStorage/API persistence.
 */
export function useWorkspaceConfig(): UseWorkspaceConfigResult {
  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const userId = user?.id ?? null;

  const [config, setConfig] = useState<WorkspaceConfigRequest | null>(null);
  const configRef = useRef<WorkspaceConfigRequest | null>(null);
  configRef.current = config;
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);

  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const serverUpdatedAtRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const userIdRef = useRef<string | null>(userId);
  userIdRef.current = userId;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    };
  }, []);

  const syncToApi = useCallback(
    async (configToSync: WorkspaceConfigRequest): Promise<boolean> => {
      const ownerId = userIdRef.current;
      if (!mountedRef.current || !ownerId) return false;

      setIsSyncing(true);
      setError(null);

      try {
        const response = await saveWorkspaceConfig(
          sanitizeWorkspaceConfigForApi(configToSync),
        );

        if (!mountedRef.current || userIdRef.current !== ownerId) return false;

        serverUpdatedAtRef.current = response.updated_at;
        saveToLocalStorage(ownerId, {
          config: configToSync,
          lastSyncedAt: new Date().toISOString(),
          serverUpdatedAt: response.updated_at,
          hasPendingChanges: false,
        });

        setHasPendingChanges(false);
        retryCountRef.current = 0;
        setIsSyncing(false);
        return true;
      } catch (err) {
        if (!mountedRef.current) return false;

        console.error("[useWorkspaceConfig] Failed to sync to API:", err);
        setError(
          err instanceof Error
            ? err
            : new Error("Failed to sync workspace config"),
        );
        setIsSyncing(false);

        if (retryCountRef.current < MAX_RETRIES) {
          retryCountRef.current++;
          retryTimeoutRef.current = setTimeout(() => {
            if (mountedRef.current && userIdRef.current === ownerId) {
              syncToApi(configToSync);
            }
          }, RETRY_DELAY_MS * retryCountRef.current);
        }

        return false;
      }
    },
    [],
  );

  const saveConfig = useCallback(
    (newConfig: WorkspaceConfigRequest) => {
      const ownerId = userIdRef.current;
      if (!ownerId) return;

      if (configsEqual(config, newConfig)) {
        return;
      }

      setConfig(newConfig);
      setHasPendingChanges(true);

      saveToLocalStorage(ownerId, {
        config: newConfig,
        lastSyncedAt: undefined,
        serverUpdatedAt: serverUpdatedAtRef.current || undefined,
        hasPendingChanges: true,
      });

      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }

      syncTimeoutRef.current = setTimeout(() => {
        syncToApi(newConfig);
      }, SYNC_DEBOUNCE_MS);
    },
    [config, syncToApi],
  );

  const forceSync = useCallback(async () => {
    if (!config) return;

    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
      syncTimeoutRef.current = null;
    }

    await syncToApi(config);
  }, [config, syncToApi]);

  useEffect(() => {
    if (isAuthLoading) {
      // Keep the current config visible during token refresh — only block the
      // first boot when we have nothing cached yet.
      if (!configRef.current) {
        setIsLoading(true);
      }
      return;
    }

    if (!isAuthenticated || !userId) {
      setConfig(null);
      setHasPendingChanges(false);
      serverUpdatedAtRef.current = null;
      setError(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const resetForUserLoad = () => {
      setConfig(null);
      setHasPendingChanges(false);
      serverUpdatedAtRef.current = null;
      setError(null);
      setIsLoading(true);
      retryCountRef.current = 0;
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = null;
      }
    };

    resetForUserLoad();

    const loadInitial = async () => {
      const localData = loadFromLocalStorage(userId);
      const initialConfig = localData?.config || null;

      if (cancelled || userIdRef.current !== userId) return;

      if (initialConfig) {
        setConfig(initialConfig);
        setHasPendingChanges(localData?.hasPendingChanges || false);
        serverUpdatedAtRef.current = localData?.serverUpdatedAt || null;
      }

      try {
        const apiResponse = await getWorkspaceConfig();

        if (cancelled || userIdRef.current !== userId) return;

        if (apiResponse) {
          const localUpdatedAt = localData?.serverUpdatedAt
            ? new Date(localData.serverUpdatedAt).getTime()
            : 0;
          const apiUpdatedAt = new Date(apiResponse.updated_at).getTime();

          if (localData?.hasPendingChanges && initialConfig) {
            setConfig(initialConfig);
            syncToApi(initialConfig);
          } else if (apiUpdatedAt >= localUpdatedAt) {
            const apiConfig = mergeWorkspaceConfigFromApi(
              {
                workspaces: apiResponse.workspaces,
                activeWorkspaceId: apiResponse.activeWorkspaceId,
                windows: apiResponse.windows,
                nextZIndex: apiResponse.nextZIndex,
                dockIconStyle: apiResponse.dockIconStyle,
                autoHideChrome: apiResponse.autoHideChrome,
                windowTransparency: apiResponse.windowTransparency,
                opaqueWindows: apiResponse.opaqueWindows,
              },
              initialConfig,
            );
            setConfig(apiConfig);
            serverUpdatedAtRef.current = apiResponse.updated_at;

            saveToLocalStorage(userId, {
              config: apiConfig,
              lastSyncedAt: new Date().toISOString(),
              serverUpdatedAt: apiResponse.updated_at,
              hasPendingChanges: false,
            });
            setHasPendingChanges(false);
          }
        } else if (initialConfig) {
          syncToApi(initialConfig);
        } else {
          const defaultConfig = getDefaultConfig();
          setConfig(defaultConfig);
          saveToLocalStorage(userId, {
            config: defaultConfig,
            hasPendingChanges: true,
          });
          setHasPendingChanges(true);
          syncToApi(defaultConfig);
        }
      } catch (err) {
        if (cancelled || userIdRef.current !== userId) return;

        console.error("[useWorkspaceConfig] Failed to fetch from API:", err);
        setError(
          err instanceof Error
            ? err
            : new Error("Failed to load workspace config"),
        );

        if (!initialConfig) {
          const defaultConfig = getDefaultConfig();
          setConfig(defaultConfig);
          saveToLocalStorage(userId, {
            config: defaultConfig,
            hasPendingChanges: true,
          });
          setHasPendingChanges(true);
        }
      } finally {
        if (!cancelled && userIdRef.current === userId && mountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    loadInitial();

    return () => {
      cancelled = true;
    };
  }, [isAuthLoading, isAuthenticated, userId, syncToApi]);

  return {
    config,
    isLoading,
    isSyncing,
    error,
    hasPendingChanges,
    saveConfig,
    forceSync,
  };
}

// Re-export for tests and logout cleanup
export { getOsStateStorageKey, LEGACY_OS_STATE_KEY } from "@/constants/osStorageKeys";
